import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import NativeService from '../services/nativeService';
import { BusFront } from 'lucide-react';
import api from '../services/api';

// Google Icon SVG Component
const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();

    // Config to toggle email login visibility
    const SHOW_EMAIL_LOGIN = false;

    const { login, loginWithToken } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();

    const isNative = NativeService.isNativePlatform();

    // Initialize Google Auth on native platform
    useEffect(() => {
        if (isNative) {
            NativeService.initGoogleAuth();
        }
    }, [isNative]);

    // Handle Google OAuth callback (web only)
    useEffect(() => {
        const token = searchParams.get('token');
        const userParam = searchParams.get('user');
        const errorParam = searchParams.get('error');

        if (errorParam) {
            const errorMessages = {
                'no_code': 'Error de autenticación con Google',
                'inactive': 'Tu cuenta está desactivada',
                'google_auth_failed': 'Error al autenticar con Google'
            };
            setError(errorMessages[errorParam] || 'Error de autenticación');
            return;
        }

        // Check for success flag (Cookie Auth) or explicit token (Legacy/Native)
        const success = searchParams.get('success');
        // token and userParam are already defined above at lines 57-58

        const handleSuccess = async () => {
            try {
                // If we have user data in params, parse it
                let userData = null;
                if (userParam) {
                    userData = JSON.parse(decodeURIComponent(userParam));
                }

                if (token) {
                    // Start session with explicit token (Native/Legacy)
                    loginWithToken(token, userData);
                    navigate('/');
                } else if (success === 'true') {
                    // Cookie-based auth: Verify session with backend
                    // CRITICAL: Clear any stale token so API uses the fresh Cookie
                    localStorage.removeItem('token');

                    try {
                        // We call /auth/me to confirm the cookie is working
                        // The Context will handle the state update if this succeeds
                        const response = await api.auth.me();
                        // IMPORTANT: api.auth.me() returns { user: {...} }, extract .user
                        loginWithToken(null, response.user); // null token relies on cookie
                        navigate('/');
                    } catch (verifyError) {
                        console.error('Session verification failed:', verifyError);
                        setError('Error verificando la sesión segura.');
                    }
                }
            } catch (e) {
                console.error('Login processing error:', e);
                setError('Error al procesar inicio de sesión');
            }
        };

        if (success === 'true' || (token && userParam)) {
            handleSuccess();
        }
    }, [searchParams, loginWithToken, toast, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate('/');
        } catch (err) {
            if (err.useGoogle) {
                setError('Esta cuenta usa Google. Por favor, inicia sesión con Google.');
            } else {
                setError(err.message || 'Error al iniciar sesión');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');

        if (isNative) {
            // Native Google Sign-In
            setGoogleLoading(true);

            // CRITICAL: Clear any existing token before starting new login to prevent "Invalid Signature" errors
            // if switching between Prod and Local environments
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            try {
                const result = await NativeService.signInWithGoogle();

                if (!result.success) {
                    if (result.reason === 'cancelled' || result.reason?.includes('cancel')) {
                        // User cancelled, no error message needed
                        setGoogleLoading(false);
                        return;
                    }
                    throw new Error(result.reason || 'Error al iniciar sesión con Google');
                }

                // Send idToken to backend for verification using fetch
                // Use production URL for native apps since they don't have a local server
                const API_BASE = isNative
                    ? 'https://calendario.xerp.es'
                    : (import.meta.env.VITE_API_URL || '');

                const fetchResponse = await fetch(`${API_BASE}/api/auth/google/native`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ idToken: result.idToken })
                });

                const response = await fetchResponse.json();

                if (response.success) {
                    loginWithToken(response.token, response.user);
                    navigate('/');
                } else {
                    throw new Error(response.error || 'Error de autenticación');
                }
            } catch (err) {
                console.error('Google native login error:', err);
                setError(err.message || 'Error al iniciar sesión con Google');
            } finally {
                setGoogleLoading(false);
            }
        } else {
            // Web OAuth redirect
            window.location.href = '/api/auth/google';
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="card">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-6">
                            <div className="w-28 h-28 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-cyan-500/20 shadow-[0_0_30px_rgba(6,182,212,0.15)] group hover:shadow-[0_0_50px_rgba(6,182,212,0.25)] transition-all duration-500">
                                <BusFront className="w-14 h-14 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent">
                            BEI Calendar
                        </h1>
                        <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Sistema de Gestión de Calendario Laboral</p>
                    </div>

                    {/* Google Login Button */}
                    <button
                        onClick={handleGoogleLogin}
                        disabled={googleLoading}
                        className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200 mb-6 shadow-md hover:shadow-lg disabled:opacity-50"
                    >
                        {googleLoading ? (
                            <>
                                <span className="animate-spin">⏳</span>
                                Conectando...
                            </>
                        ) : (
                            <>
                                <GoogleIcon />
                                Continuar con Google
                            </>
                        )}
                    </button>

                    {/* Error display - always visible */}
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm mb-4">
                            {error}
                        </div>
                    )}

                    {/* Divider */}
                    {SHOW_EMAIL_LOGIN && (
                        <div className="relative mb-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t" style={{ borderColor: 'var(--border-color)' }}></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4" style={{ background: 'var(--bg-card)', color: 'var(--text-muted)' }}>o con email</span>
                            </div>
                        </div>
                    )}

                    {/* Form */}
                    {SHOW_EMAIL_LOGIN && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: 'var(--accent-label)' }}>
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@email.com"
                                    required
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-xs uppercase tracking-wider font-medium mb-2" style={{ color: 'var(--accent-label)' }}>
                                    Contraseña
                                </label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    className="input-field"
                                />
                            </div>

                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full disabled:opacity-50"
                            >
                                {loading ? '⏳ Iniciando...' : 'Iniciar Sesión'}
                            </button>
                        </form>
                    )}

                    {/* Footer */}
                    <div className="text-center mt-8 pt-6 border-t text-sm" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                            <Link to="/privacidad" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }}>Privacidad</Link>
                            <Link to="/cookies" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }}>Cookies</Link>
                            <Link to="/legal" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.8rem' }}>Aviso Legal</Link>
                        </div>
                        © 2026 TUVISA Calendar — calendario.xerp.es
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
